'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isProduction } from '../services/env';
import mapFieldsToState from '../lib/mapFieldsToState';
import getEnabledFields from '../lib/getEnabledFields';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { formatPriceInput } from '../services/formatPrice';
import getPaymentMethod from '../lib/getPaymentMethod';
import { useQueryClient } from '@tanstack/react-query';
import getSessionId from '../lib/getSessionId';
import getAuthToken from '../lib/getAuthToken';
import { submissionKey, paymentKey, subscriptionKey } from '../queryKeys';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import { draftPaymentGuests, savedGuest } from '../lib/guests/checkoutGuests';
import getCheckoutPath from '../lib/getCheckoutPath';
import getSellerSlug from '../services/getSellerSlug';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getPaymentOption from '../lib/getPaymentOption';
import useCheckoutForm from './useCheckoutForm';
import useCheckoutRouter from './useCheckoutRouter';
import { checkoutTypes } from '../enums/checkout-types';
import { checkoutLayouts } from '../enums/checkout-layouts';
import getPageTheme from '../lib/theming/getPageTheme';
import useDebouncedCallback from './useDebouncedCallback';
import api from '../api';
import { PAGE_TYPES } from '../enums/page-types';
import getFormFieldValues from '../lib/getFormFieldValues';
import getFormVariantValues from '../lib/getFormVariantValues';
import {
  selectablePrices,
  resolveSelectedPrice,
  getInitialSelectedPriceId,
  isRecurringPurchase,
  isMultiPrice,
} from '../lib/multiPrice';
import { queryPriceField } from '../lib/discountedFrom';
import isPaymentPlanPrice from '../lib/isPaymentPlanPrice';
import getTicketTypeValues from '../lib/getTicketTypeValues';
import getTicketPwywAmountValues from '../lib/getTicketPwywAmountValues';
import getUserFacingPaymentError from '../lib/getUserFacingPaymentError';
import checkIsCoverProcessingFeeSelected from '../lib/checkCoverProcessingFee';

/**
 * The current URL without its checkoutEventId parameter, or null when it has none.
 * The hash is kept: the dashboard renders this checkout as a preview inside its own
 * routes, where the hash points at an editor section.
 */
export function urlWithoutCheckoutEventId(href) {
  const url = new URL(href);
  if (!url.searchParams.has('checkoutEventId')) return null;
  url.searchParams.delete('checkoutEventId');
  return url.pathname + url.search + url.hash;
}

export const checkoutStates = {
  INITIAL: 'initial',
  SUBMITTING: 'submitting',
  CREATING_SUBMISSION: 'creating_submission',
  CREATING_PAYMENT: 'creating_payment',
  CREATING_SUBSCRIPTION: 'creating_subscription',
  PROCESSING: 'processing',
  PROCESSING_PAYMENT: 'processing_payment',
  PROCESSING_SUBSCRIPTION: 'processing_subscription',
  ERROR: 'error',
  PENDING: 'pending',
  COMPLETED: 'completed',
};

export { checkoutLayouts };

const DELAY_PREFILLED_DRAFT_CREATION_MS = 60000;

function getInitialCheckoutState(query) {
  const { payment_intent, payment_intent_client_secret } = query;

  if (payment_intent && payment_intent_client_secret) {
    return checkoutStates.PROCESSING_PAYMENT;
  }

  return checkoutStates.INITIAL;
}

function getAddressMode({ shippingAddressCollection, billingAddressCollection }) {
  if (shippingAddressCollection) {
    return 'shipping';
  } else if (billingAddressCollection) {
    return 'billing';
  } else {
    return null;
  }
}

function getCheckoutLayout(checkoutLayout, checkoutType) {
  if (checkoutType === checkoutTypes.EMBED) {
    return checkoutLayouts.CENTER;
  } else if (checkoutType === checkoutTypes.CHECKOUT || checkoutType === checkoutTypes.IFRAME) {
    return checkoutLayout;
  } else {
    return checkoutLayouts.CENTER;
  }
}

const CheckoutStateContext = React.createContext({});

const CheckoutStateProvider = ({ value = {}, children }) => {
  const {
    checkout: rawCheckout = {},
    product = {},
    seller = {},
    options: options = {
      type: checkoutTypes.CHECKOUT,
    },
    upsell = {},
    isUpsell = false,
    isPreview = false,
    isTesting = false,
    isTestingPayments = false,
    isTestMode = false,
    isMeasurer = false,
    isScreenshot = false,
    query = {},
    params = {},
    countryCode,
    iframeId,
    isConfirmation = false,
    publicKey,
    publicKeyTest,
    previewDeviceSize = null, // For preview mode responsive behavior
    previewColorMode = null, // For preview mode color scheme override
    previewSelectedPriceId = null, // Preview-only: force the picker to a chosen price
    draftCheckout = null,
  } = value;

  // Every consumer reads fields off `checkout` (aliased as `page` below), so
  // dropping disabled fields here keeps the preview and the live page identical.
  const checkout = useMemo(
    () =>
      Array.isArray(rawCheckout.fields)
        ? { ...rawCheckout, fields: getEnabledFields(rawCheckout.fields) }
        : rawCheckout,
    [rawCheckout],
  );

  const checkoutType = useMemo(() => options.type, [options.type]);

  const checkoutLayout = useMemo(
    () => getCheckoutLayout(checkout.hostedLayoutType, checkoutType),
    [checkout.hostedLayoutType, checkoutType],
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [payment, setPayment] = useState(null);
  const [customer, setCustomer] = useState({});
  const [paymentGateway, setPaymentGateway] = useState(Object.keys(checkout.paymentMethods)[0]);

  // selectedPriceId — the price the customer has chosen from PricePicker.
  // Pre-selected only when the merchant set an explicit default; with no default
  // the checkout opens with nothing selected so the customer makes a pricing
  // decision. Single-price products have no picker — useSelectedPrice /
  // useProductPrice resolve the lone price regardless of selectedPriceId.
  const [selectedPriceId, setSelectedPriceId] = useState(() =>
    getInitialSelectedPriceId({
      product,
      query,
      draftCheckout,
      isPreview,
      previewSelectedPriceId,
      allowDynamicPreselectedPrice: checkout.allowDynamicPreselectedPrice,
    }),
  );

  // The buyer's EFFECTIVE selected price id: the explicit selection, or the lone
  // selectable price on a single-price product (no picker). Null when a
  // multi-price product has no (or a cleared) choice. Used to scope variants to a
  // price — a price-scoped variant hides until a price is chosen. The merchant
  // default is seeded into selectedPriceId at init (above), so no runtime default
  // fallback is passed — a cleared selection (e.g. switching to a tab without the
  // selected price) resolves to null. Mirrors useSelectedPrice.
  const effectiveSelectedPriceId = useMemo(() => {
    const resolved = resolveSelectedPrice(
      selectablePrices(product?.prices ?? [], query),
      selectedPriceId,
      null,
    );
    return resolved ? String(resolved._id) : null;
  }, [product, selectedPriceId, query]);

  const [paymentOption, setPaymentOption] = useState(getPaymentOption(checkout.paymentOptions));
  const [paymentMethod, setPaymentMethod] = useState(
    getPaymentMethod(checkout.paymentMethods, 'stripe'),
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [paymentError, setPaymentError] = useState('');
  const [formError, setFormErrorState] = useState('');
  const setFormError = message =>
    setFormErrorState(getUserFacingPaymentError(message, checkout.type));
  const [payWhatYouWantPrice, setPayWhatYouWantPrice] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [showCouponCodeField, setShowCouponCodeField] = useState(
    checkout.showCouponCodeFieldType === 'field' || !!query?.coupon || !!draftCheckout?.coupon, // if using type 'field', show field immediately or if coupon is present
  );

  const { form, setForm } = useCheckoutForm({
    checkout,
    product,
    query,
    countryCode,
    onPayWhatYouWantPriceChange: setPayWhatYouWantPrice,
    draftCheckout,
    isPreview,
    pwywPriceReference: (product?.prices ?? []).find(
      p => String(p._id) === String(effectiveSelectedPriceId),
    )?.reference,
    isMultiPriceProduct: isMultiPrice(product?.prices, query),
    selectedPriceId: effectiveSelectedPriceId,
  });

  const [address, setAddress] = useState({
    mode: getAddressMode({
      shippingAddressCollection: checkout.shippingAddressCollection,
      billingAddressCollection: checkout.billingAddressCollection,
    }),
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });

  const [visitId, setVisitId] = useState('');
  const [trackedEvents, setTrackedEvents] = useState([]);
  const [state, setState] = useState(getInitialCheckoutState(query));

  const [layoutId, setLayoutId] = useState(null);
  const [freeTrialDuration, setFreeTrialDuration] = useState(1);
  const [freeTrialDurationType, setFreeTrialDurationType] = useState('day');
  const [isChecked, setIsChecked] = useState(false);
  const [addressMode, setAddressMode] = useState(getAddressMode(checkout));
  const [orderNote, setOrderNote] = useState('');
  const [showPaymentOption, setShowPaymentOption] = useState(false);
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [stripeAddress, setStripeAddress] = useState({});
  const hasFocusedCheckoutRef = useRef(false);
  const hasInitiatedPaymentRef = useRef(false);
  const isSubmittingDraftRef = useRef(false);
  const mountTimestampRef = useRef(Date.now());
  // Multi-price abandonment (Option 1): a per-checkout-attempt id shared by this
  // cart's charge + subscription drafts. Adopt it from a recovered draft (so
  // post-recovery price switches target the same attempt) else mint it lazily on
  // the first draft write — client-only, avoiding SSR/hydration churn. Distinct
  // from the analytics getSessionId().
  const checkoutAttemptIdRef = useRef(draftCheckout?.checkoutAttemptId || null);
  const pendingDraftTimeoutRef = useRef(null);
  // When a customer picks a pay-what-you-want option in the multi-price picker,
  // its amount input should focus so they can type immediately. Switching
  // billing modes re-keys Stripe Elements and remounts the whole checkout
  // (see PricePicker's tab comment), which discards a focus set synchronously in
  // the click handler. This ref carries a { priceId, ts } "focus this next"
  // signal that survives the remount because the provider lives above it; the
  // PWYW input consumes it on mount. ts bounds it so a stale signal can't steal
  // focus on an unrelated later remount.
  const pendingPwywFocusRef = useRef(null);

  const formRef = useRef(null);
  const queryClient = useQueryClient();
  const [isUpsellRetry, setIsUpsellRetry] = useState(false);
  const router = useCheckoutRouter();

  /**
   * We delete the checkoutEventId query parameter client side for simplicity.
   * During SSR in order to delete the query parameter we would need to do a redirect.
   * This has two disadvantages
   * 1. The redirect means we have to call the `getPages` endpoint twice (slow)
   * 2. Our routing is complex and knowing the exact path to redirect to isn't reliable because we have hosted pages, embeds and overlays.
   */
  useEffect(() => {
    if (!draftCheckout) {
      const next = urlWithoutCheckoutEventId(window.location.href);
      if (next) window.history.replaceState(window.history.state, '', next);
    }
  }, [draftCheckout]);

  const dynamicPrice = useMemo(() => {
    if (!checkout.allowDynamicPrice) {
      return null;
    }

    // Per-price override: `${reference}_price` targets the selected price. The legacy
    // unprefixed `price` param carries no reference, so it is single-price only — on a
    // multi-price product it would reprice every option at once. The resolved amount
    // flows into the charge/subscription body and is re-gated server-side against the
    // merchant's allowDynamicPrice setting.
    const selectedPrice = (product?.prices ?? []).find(
      p => String(p._id) === String(effectiveSelectedPriceId),
    );
    const override = queryPriceField(selectedPrice?.reference, 'price', query, {
      allowLegacyFallback: !isMultiPrice(product?.prices, query),
    });

    return override != null ? Number(override) : null;
  }, [checkout, query, product, effectiveSelectedPriceId]);

  const dynamicPlanIterations = useMemo(() => {
    if (!checkout.allowDynamicPlanIterations) return null;

    const selectedPrice = (product?.prices ?? []).find(
      p => String(p._id) === String(effectiveSelectedPriceId),
    );

    // Multi-price: `${reference}_no_of_payments` only, and only for a price that is
    // ALREADY a payment plan. The merchant fixes the billing shape per price, so the
    // URL may change a plan's length but must never convert a subscription into one —
    // unlike a single-price product, where switching billing shape is the whole point
    // of the param.
    if (isMultiPrice(product?.prices, query)) {
      if (!isPaymentPlanPrice(selectedPrice)) return null;
      const perPrice = queryPriceField(selectedPrice?.reference, 'no_of_payments', query, {
        allowLegacyFallback: false,
      });
      return perPrice ? Number(perPrice) : null;
    }

    // TODO(M14): legacy product.type fallback lives in isRecurringPurchase — remove after the backfill.
    if (!isRecurringPurchase(selectedPrice, product)) return null;
    return query.no_of_payments ? Number(query.no_of_payments) : null;
  }, [checkout, query, product, effectiveSelectedPriceId]);

  useMemo(() => {
    // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
    setPayWhatYouWantPrice(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant]);

  // update showCouponCodeField if changed
  useEffect(() => {
    setShowCouponCodeField(
      checkout.showCouponCodeFieldType === 'field' ||
        !!query?.coupon ||
        !!draftCheckout?.coupon ||
        !!coupon,
    );
  }, [checkout.showCouponCodeFieldType, query?.coupon, draftCheckout?.coupon, coupon]);

  // update paymentOption if changed
  useEffect(() => {
    if (!checkout.paymentOptions) {
      return;
    }

    const selectedPaymentOptionExists = !!checkout.paymentOptions.find(
      option => option.enabled === true && option._id === paymentOption._id,
    );

    if (paymentOption === null) {
      // if paymentOption is null, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else if (!selectedPaymentOptionExists) {
      // if the selected payment option no longer exists, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else {
      // set the currently selected payment option based on new data
      const updatedPaymentOption = checkout.paymentOptions.find(
        option => option._id === paymentOption._id,
      );

      setPaymentOption(updatedPaymentOption);
    }
  }, [checkout.paymentOptions]);

  // Payment options are a one-time-only feature. When the purchase resolves to
  // recurring (selected price's billingType, legacy product.type fallback), a
  // previously chosen manual/partial option is cleared back to a full card
  // payment: the picker is hidden on the same gate (CheckoutForm), the card
  // element must come back (checkShouldShowPaymentMethods hides it while a
  // manual option is selected), and the server ignores paymentOption on the
  // subscription path anyway.
  useEffect(() => {
    const selectedPrice = (product?.prices ?? []).find(
      p => String(p._id) === String(effectiveSelectedPriceId),
    );

    // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase)
    if (!isRecurringPurchase(selectedPrice, product)) {
      return;
    }

    if (paymentOption?.type && paymentOption.type !== 'full') {
      setPaymentOption({ type: 'full' });
    }

    if (selectedPaymentMethod === 'manual') {
      setSelectedPaymentMethod('card');
    }
  }, [effectiveSelectedPriceId, product, paymentOption, selectedPaymentMethod]);

  useEffect(() => {
    if (!visitId) {
      return;
    }

    updateDraftChargeDebounced();
  }, [visitId]);

  // Persist the customer's price selection to the abandonment draft so it can be
  // restored on recovery. The valid-email / bot-delay guards inside
  // updateDraftCharge still gate whether a draft is actually written.
  useEffect(() => {
    updateDraftChargeDebounced();
  }, [selectedPriceId]);

  // Saving a guest is kept on the draft too; the same guards decide whether it is written.
  const savedGuests = JSON.stringify(
    (form.guests ?? []).filter(guest => guest.done).map(savedGuest),
  );
  useEffect(() => {
    if (savedGuests !== '[]') updateDraftChargeDebounced();
  }, [savedGuests]);

  useEffect(() => {
    return () => {
      if (pendingDraftTimeoutRef.current) {
        clearTimeout(pendingDraftTimeoutRef.current);
      }
    };
  }, []);

  async function handleSetSubmission(submission) {
    setSubmission(submission);
    setCustomer(submission.customer);

    const sessionId = getSessionId();

    // Update the submission in the query cache
    queryClient.setQueryData(submissionKey(submission._id, sessionId), submission);
  }

  function handleSetPayment(payment) {
    // Update the payment in the state
    setPayment(payment);
    setCustomer(payment.customer);

    const sessionId = getSessionId();

    // Prices-first: pick the cache key from the SELECTED price's billing type.
    const selectedPrice = (product?.prices ?? []).find(
      p => String(p._id) === String(effectiveSelectedPriceId),
    );
    // TODO(M14): legacy product.type fallback lives in isRecurringPurchase — remove after the backfill.
    const key = isRecurringPurchase(selectedPrice, product)
      ? subscriptionKey(payment._id, sessionId)
      : paymentKey(payment._id, sessionId);

    queryClient.setQueryData(key, payment);
  }

  function resetForm() {
    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);
  }

  function removeCoupon() {
    setCoupon(null);
  }

  function restartPayment({ isConfirmation = false } = {}) {
    // same as resetForm except we keep the paymentIntent
    // which is needed for redirect based payment methods
    // if the payment fails and checkout gets reloaded, it will have a new payment intent created on server
    // if we then use resetForm (on clicking Try again), the payment intent is removed from the checkout
    // and the purchase creates a new payment intent somehow, which results in orphan purchases
    // @todo we can combine this again once we launch payment element without payment intents

    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);

    // navigate back from confirmation to the checkout/form page
    if (isConfirmation) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);

      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }

  const theme = useMemo(() => {
    return getPageTheme({
      theme: checkout.theme,
      themeOverrides: checkout.themeOverrides,
      seller: checkout.seller,
    });
  }, [checkout.theme, checkout.themeOverrides, checkout.seller]);

  function getDraftPaymentData() {
    const { fields, name, email } = getFormFieldValues(form.fields, checkout.fields);
    const variants = getFormVariantValues(
      product.variants,
      form.variants,
      undefined,
      effectiveSelectedPriceId,
    );
    const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);
    const ticketPwywAmounts = getTicketPwywAmountValues(
      checkout.ticketGroups,
      form.ticketGroups,
      checkout.eventDetails?.currency || product?.currency,
    );
    const url = new URL(window.location.href);
    const checkoutEventId = url.searchParams.get('checkoutEventId');

    /**
     * Check for all fields that are an email element for their references. Check if the query
     * Has the email reference and a value. We do not validate if the email is valid.
     * This still shows intent of being prefilled and even with validation the API might not match.
     */
    const emailFields = (fields ?? []).filter(field => field.element === 'email');
    const emailFieldReferences = emailFields.map(field => field.reference);
    const queryHasEmail = emailFieldReferences.some(
      reference => reference != null && query[reference] != null,
    );
    const isPrefilled = draftCheckout?.isPrefilled === true || queryHasEmail === true;

    const hasInteraction =
      draftCheckout?.hasInteraction === true || hasFocusedCheckoutRef.current === true;

    return {
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants,
      tickets,
      ticketPwywAmounts,
      guests: draftPaymentGuests(checkout, form),
      sellerId: seller._id,
      ...(isTestingPayments && { testingPayments: true }),
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
      couponId: coupon?._id || null,
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption: paymentOption,
      coverProcessingFee: checkIsCoverProcessingFeeSelected({
        page: checkout,
        form,
        paymentOption,
      }),
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      ...(effectiveSelectedPriceId && { priceId: effectiveSelectedPriceId }),
      ...(payWhatYouWantPrice && { payWhatYouWantPrice }),
      ...(dynamicPrice && { dynamicPrice }),
      ...(hasInteraction && { hasInteraction }),
      ...(isPrefilled && { isPrefilled }),
    };
  }

  /**
   * updateDraftCharge is defined here in useCheckoutState
   * to ensure a single debounced instance is shared across all components.
   * This prevents duplicate draft creation when multiple components trigger updates.
   */
  async function updateDraftCharge() {
    if (hasInitiatedPaymentRef.current === true) {
      return null;
    }

    if (isSubmittingDraftRef.current) {
      return null;
    }

    const paymentData = getDraftPaymentData();

    /**
     * This logic slows down the initial creation of a prefilled draft to 60 seconds.
     * If isPrefilled is true and hasInteraction is not true an update is scheduled for the next 60s.
     *
     * If hasInteraction is true we clear pendingDraftTimeoutRef and set the ref back to null.
     *
     *
     * We have added this change to combat bots visiting checkouts with query params prefilled.
     * When a bot did this, it created a draft instantly and never interacted
     * With the page further.
     */
    const elapsedTime = Date.now() - mountTimestampRef.current;
    if (
      paymentData.isPrefilled === true &&
      paymentData.hasInteraction !== true &&
      elapsedTime < DELAY_PREFILLED_DRAFT_CREATION_MS
    ) {
      if (!pendingDraftTimeoutRef.current) {
        const remainingTime = DELAY_PREFILLED_DRAFT_CREATION_MS - elapsedTime;
        pendingDraftTimeoutRef.current = setTimeout(() => {
          pendingDraftTimeoutRef.current = null;
          updateDraftChargeDebounced();
        }, remainingTime);
      }

      return null;
    } else {
      if (pendingDraftTimeoutRef.current) {
        clearTimeout(pendingDraftTimeoutRef.current);
        pendingDraftTimeoutRef.current = null;
      }
    }

    isSubmittingDraftRef.current = true;

    // Don't fire draft charge in preview mode - check multiple ways
    const isInPreviewMode = isPreview || query?.preview === 'true' || query?.preview === true;
    const isForm = checkout.type === PAGE_TYPES.FORM;

    /**
     * Prevent the creation or updating of a draft if the checkout is
     * currently submitting, creating, processing or completed a payment.
     * This prevents an issue where the draft changes the windows history causing a payment to hang on the
     * payment page despite having been paid/completed in some capacity.
     */
    const isSubmittingOrProcessing =
      state === checkoutStates.SUBMITTING ||
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION ||
      state === checkoutStates.PROCESSING_PAYMENT ||
      state === checkoutStates.COMPLETED;

    if (isInPreviewMode || isForm || isSubmittingOrProcessing || isUpsell || isTesting) {
      isSubmittingDraftRef.current = false;
      return null;
    }

    // Only fire API call if there's a valid email field with a valid email address
    const emailField = paymentData.fields?.find(field => field.type === 'email' && field.value);
    const isValidEmail = emailField && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailField.value);

    if (!isValidEmail) {
      isSubmittingDraftRef.current = false;
      return null;
    }

    try {
      if (!checkoutAttemptIdRef.current) {
        checkoutAttemptIdRef.current =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `ca_${mountTimestampRef.current}_${Math.random().toString(36).slice(2)}`;
      }

      const draftChargeEvent = await api.createOrUpdateCheckoutEvent(
        checkout._id,
        { ...paymentData, checkoutAttemptId: checkoutAttemptIdRef.current },
        paymentData.checkoutEventId || null,
        isTestingPayments ? getAuthToken() : null,
      );

      if (draftChargeEvent?.data?._id) {
        const url = new URL(window.location.href);
        url.searchParams.set('checkoutEventId', draftChargeEvent.data._id);
        window.history.replaceState({}, '', url.pathname + url.search);
      }

      return draftChargeEvent;
    } finally {
      isSubmittingDraftRef.current = false;
    }
  }

  const [updateDraftChargeDebounced, cancelUpdateDraftCharge] = useDebouncedCallback(
    updateDraftCharge,
    1000,
  );

  const checkoutState = {
    page: checkout,
    checkout,
    theme,
    product,
    seller,
    dynamicPrice,
    dynamicPlanIterations,
    isProcessing,
    setIsProcessing,
    submission,
    setSubmission: handleSetSubmission,
    payment,
    setPayment: handleSetPayment,
    paymentGateway,
    setPaymentGateway,
    paymentOption,
    setPaymentOption,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    selectedPriceId,
    setSelectedPriceId,
    pendingPwywFocusRef,
    effectiveSelectedPriceId,
    paymentError,
    setPaymentError,
    formError,
    setFormError,
    payWhatYouWantPrice,
    setPayWhatYouWantPrice,
    coupon,
    setCoupon,
    removeCoupon,
    form,
    setForm,
    address,
    setAddress,
    checkoutType,
    upsell,
    isUpsell,
    isUpsellRetry,
    setIsUpsellRetry,
    isTesting,
    isTestingPayments,
    isPreview,
    isMeasurer,
    visitId,
    setVisitId,
    trackedEvents,
    setTrackedEvents,
    state,
    setState,
    customer,
    setCustomer,
    query,
    params,
    resetForm,
    restartPayment,
    layoutId,
    setLayoutId,
    freeTrialDuration,
    setFreeTrialDuration,
    freeTrialDurationType,
    setFreeTrialDurationType,
    isChecked,
    setIsChecked,
    addressMode,
    setAddressMode,
    showCouponCodeField,
    setShowCouponCodeField,
    orderNote,
    setOrderNote,
    showPaymentOption,
    setShowPaymentOption,
    activeSidePanel,
    setActiveSidePanel,
    stripeAddress,
    setStripeAddress,
    hasFocusedCheckoutRef,
    hasInitiatedPaymentRef,
    formRef,
    isScreenshot,
    isTestMode,
    countryCode,
    iframeId,
    checkoutLayout,
    isConfirmation,
    publicKey,
    publicKeyTest,
    previewDeviceSize,
    previewColorMode,
    currencyLocale: theme?.locale,
    draftCheckout,
    updateDraftCharge,
    updateDraftChargeDebounced,
    cancelUpdateDraftCharge,
  };

  // only attach to window on client
  if (!isProduction && typeof window !== 'undefined') {
    window.checkoutState = checkoutState;
  }

  return (
    <CheckoutStateContext.Provider value={checkoutState}>{children}</CheckoutStateContext.Provider>
  );
};

export default function useCheckoutState() {
  const context = React.useContext(CheckoutStateContext);

  if (context === undefined) {
    throw new Error('useCheckoutState must be used within a CheckoutStateProvider');
  }

  return context;
}

export { CheckoutStateContext, CheckoutStateProvider };
